import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  BookOpen, Sparkles, Gift, Share2, Crown, Star, Zap, Check, X,
  PenTool, Users, FileText, Mic, Download, Shield, ChevronRight,
  ArrowRight, Play, Pause, Volume2, HelpCircle, Feather, Award,
  BookMarked, Map, Stethoscope, Eye, Wand2, Palette, RefreshCw,
  Clock, BarChart3, LogOut, MoreVertical, ExternalLink, Lightbulb,
  ChevronDown, ChevronUp, Send, MessageSquare, ThumbsUp
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
import { toast } from "sonner";
import ShweebumCat from "../components/ShweebumCat";
import ShareRewardsModal from "../components/ShareRewardsModal";
import NewProjectDialog from "../components/NewProjectDialog";
import { useAuth } from "../components/Auth/AuthProvider";
import { API } from "../lib/api";

// Scrolling banner component for Plus/Premium features
const ScrollingBanner = ({ features, tier, gradient, textColor, speed = 30 }) => {
  const [offset, setOffset] = useState(0);
  const containerRef = useRef(null);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setOffset(prev => prev - 1);
    }, speed);
    return () => clearInterval(interval);
  }, [speed]);

  // Reset offset when it scrolls past content
  const resetThreshold = -features.length * 250;
  const displayOffset = offset < resetThreshold ? 0 : offset;
  
  useEffect(() => {
    if (offset < resetThreshold) setOffset(0);
  }, [offset, resetThreshold]);

  return (
    <div className={`relative overflow-hidden py-3 ${gradient}`}>
      {/* Fade edges */}
      <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-black/80 to-transparent z-10" />
      <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-black/80 to-transparent z-10" />
      
      <div 
        ref={containerRef}
        className="flex items-center gap-8 whitespace-nowrap"
        style={{ transform: `translateX(${displayOffset}px)` }}
      >
        {/* Duplicate features for seamless loop */}
        {[...features, ...features, ...features].map((feature, idx) => (
          <div key={idx} className="flex items-center gap-3 px-4">
            <span className={`text-sm font-medium ${textColor}`}>{feature}</span>
            <span className={`text-xs opacity-60 ${textColor}`}>{tier}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Before/After Writing Example Component
const WritingExample = ({ before, after, label, isPremium = false }) => (
  <div className="bg-slate-900/60 rounded-xl border border-slate-700/50 overflow-hidden">
    <div className="flex items-center justify-between px-4 py-2 bg-slate-800/50 border-b border-slate-700/50">
      <span className="text-sm font-medium text-slate-300">{label}</span>
      {isPremium && (
        <Badge className="bg-violet-500/20 text-violet-300 border-violet-500/30 text-xs">
          <Crown className="w-3 h-3 mr-1" />
          Nova
        </Badge>
      )}
    </div>
    <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-700/50">
      <div className="p-4">
        <p className="text-xs text-slate-500 mb-2 uppercase tracking-wide">Before</p>
        <p className="text-slate-400 text-sm italic leading-relaxed">{before}</p>
      </div>
      <div className="p-4 bg-gradient-to-r from-transparent to-cyan-950/20">
        <p className="text-xs text-cyan-400 mb-2 uppercase tracking-wide">After Shweebum</p>
        <p className="text-slate-200 text-sm leading-relaxed">{after}</p>
      </div>
    </div>
  </div>
);

// Testimonial Card Component
const TestimonialCard = ({ name, location, quote, avatar }) => (
  <Card className="bg-slate-900/40 border-slate-700/40 hover:border-cyan-500/30 transition-all">
    <CardContent className="p-5">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500/30 to-violet-500/30 flex items-center justify-center text-white font-bold text-sm border border-cyan-500/20">
          {name.split(' ').map(n => n[0]).join('')}
        </div>
        <div>
          <p className="text-white font-medium text-sm">{name}</p>
          <p className="text-slate-500 text-xs">{location}</p>
        </div>
      </div>
      <p className="text-slate-300 text-sm italic leading-relaxed">"{quote}"</p>
      <div className="flex items-center gap-1 mt-3">
        {[1,2,3,4,5].map(i => (
          <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
        ))}
      </div>
    </CardContent>
  </Card>
);

// Tutorial Card Component
const TutorialCard = ({ title, description, duration, tier, icon: Icon, onClick }) => (
  <div 
    className="bg-slate-900/50 border border-slate-700/50 rounded-xl p-4 hover:border-cyan-500/30 transition-all cursor-pointer group"
    onClick={onClick}
  >
    <div className="flex items-start gap-3">
      <div className="w-12 h-12 rounded-lg bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center shrink-0 group-hover:bg-cyan-500/25 transition-colors">
        <Icon className="w-6 h-6 text-cyan-400" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h4 className="text-white font-medium text-sm truncate">{title}</h4>
          {tier === 'free' && (
            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-[10px]">FREE</Badge>
          )}
          {tier === 'plus' && (
            <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30 text-[10px]">PLUS</Badge>
          )}
          {tier === 'premium' && (
            <Badge className="bg-violet-500/20 text-violet-400 border-violet-500/30 text-[10px]">NOVA</Badge>
          )}
        </div>
        <p className="text-slate-400 text-xs line-clamp-2">{description}</p>
        <p className="text-slate-500 text-xs mt-1 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {duration}
        </p>
      </div>
      <Play className="w-8 h-8 text-cyan-500/50 group-hover:text-cyan-400 transition-colors shrink-0" />
    </div>
  </div>
);

const FreebumPage = () => {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewProject, setShowNewProject] = useState(false);
  const [showShareRewards, setShowShareRewards] = useState(false);
  const FALLBACK_GENRES = [
    {id:"fantasy",name:"Fantasy"},{id:"romance",name:"Romance"},{id:"mystery",name:"Mystery"},
    {id:"thriller",name:"Thriller"},{id:"scifi",name:"Science Fiction"},{id:"horror",name:"Horror"},
    {id:"literary",name:"Literary Fiction"},{id:"historical",name:"Historical Fiction"},
    {id:"adventure",name:"Adventure"},{id:"drama",name:"Drama"},{id:"comedy",name:"Comedy"},
    {id:"dystopian",name:"Dystopian"},{id:"crime",name:"Crime"},{id:"urban",name:"Urban Fiction"},
    {id:"ya",name:"Young Adult"},{id:"paranormal",name:"Paranormal"},{id:"western",name:"Western"},
    {id:"spiritual",name:"Spiritual"},{id:"action",name:"Action"},{id:"dark_fiction",name:"Dark Fiction"},
  ];
  const [genres, setGenres] = useState(FALLBACK_GENRES);
  const [usage, setUsage] = useState(null);
  const [hasClaimedReward, setHasClaimedReward] = useState(false);
  const [showTutorial, setShowTutorial] = useState(null); // Tutorial modal state
  
  // Dropdown states for collapsible sections
  const [tutorialsOpen, setTutorialsOpen] = useState(false);
  const [reviewsOpen, setReviewsOpen] = useState(false);
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const [plansOpen, setPlansOpen] = useState(false);
  
  // Review submission state
  const [reviewForm, setReviewForm] = useState({
    email: '',
    rating: 5,
    recommend: true,
    reviewText: ''
  });
  const [submittingReview, setSubmittingReview] = useState(false);

  // Freebum limits
  const FREEBUM_LIMITS = {
    projects: 2,
    chaptersPerProject: 20,
    aiGenerations: 10,
    pdfExports: 3,
  };

  useEffect(() => {
    if (!user) {
      navigate("/");
      return;
    }
    fetchProjects();
    fetchGenres();
    fetchUsage();
    checkRewardStatus();
  }, [user, navigate]);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/projects/${user.id}`);
      setProjects(res.data);
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGenres = async () => {
    try {
      const res = await axios.get(`${API}/genres`);
      setGenres(res.data.genres);
    } catch (error) {
      console.error("Error fetching genres:", error);
    }
  };

  const fetchUsage = async () => {
    try {
      const res = await axios.get(`${API}/usage/${user.id}`);
      setUsage(res.data);
    } catch (error) {
      console.error("Error fetching usage:", error);
    }
  };

  const checkRewardStatus = async () => {
    try {
      const res = await axios.get(`${API}/user/${user.id}/share-reward-status`);
      setHasClaimedReward(res.data.claimed);
    } catch (err) {
      const claimed = localStorage.getItem(`shweebum_share_reward_${user?.id}`);
      setHasClaimedReward(claimed === 'true');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('shweebum_user');
    localStorage.removeItem('shweebum_token');
    sessionStorage.clear();
    window.location.replace('/');
  };

  // Submit review function
  const handleSubmitReview = async () => {
    if (!reviewForm.email || !reviewForm.reviewText) {
      toast.error("Please fill in your email and review");
      return;
    }
    
    setSubmittingReview(true);
    try {
      await axios.post(`${API}/contact`, {
        name: user?.name || 'Freebum User',
        email: reviewForm.email,
        subject: `Review Submission - ${reviewForm.rating} Stars - ${reviewForm.recommend ? 'Recommends' : 'Does Not Recommend'}`,
        message: `REVIEW SUBMISSION FROM FREEBUM PAGE

Rating: ${'★'.repeat(reviewForm.rating)}${'☆'.repeat(5 - reviewForm.rating)} (${reviewForm.rating}/5)
Recommends Shweebum: ${reviewForm.recommend ? 'YES' : 'NO'}
User Email: ${reviewForm.email}
User Account: ${user?.email || 'Not logged in'}

Review:
${reviewForm.reviewText}

---
Submitted via Freebum Review Form`
      });
      
      toast.success("Thank you! Your review has been submitted and will be reviewed before being displayed.");
      setReviewForm({ email: '', rating: 5, recommend: true, reviewText: '' });
    } catch (error) {
      toast.error("Failed to submit review. Please try again.");
    } finally {
      setSubmittingReview(false);
    }
  };

  // Calculate usage stats
  const aiGenerationsUsed = usage?.chapters_generated_this_month || 0;
  const pdfExportsUsed = parseInt(localStorage.getItem(`shweebum_pdf_exports_${user?.id}`) || '0');
  const projectCount = projects.length;

  // Plus features for scrolling banner
  const plusFeatures = [
    "100,000 words/month",
    "Shweebum Nova Intelligence",
    "Premium Voice Reader",
    "10 Book Covers/month",
    "PDF & DOCX Export",
    "KDP Publishing Validator",
    "Email Support",
    "10 Projects"
  ];

  // Premium features for scrolling banner
  const premiumFeatures = [
    "300,000 words/month",
    "FULL BOOK in One Click",
    "Continue My Style",
    "Unlimited Voice Reader",
    "Unlimited Book Covers",
    "Priority Support",
    "Smart Refine",
    "Auto-Polish",
    "Unlimited Projects"
  ];

  // Tutorials with content
  const tutorials = [
    { 
      id: "first-book",
      title: "Creating Your First Book", 
      description: "Start from an idea and build your story outline", 
      duration: "3 min", 
      tier: "free", 
      icon: BookOpen,
      content: [
        "1. Click 'Start New Book' on the Freebum home page",
        "2. Choose 'Start Writing' for a blank canvas or 'Story Builder' for guided help",
        "3. Give your book a title — this can be changed later",
        "4. In Story Builder, describe your idea in a few sentences",
        "5. The wizard will help you create a basic outline",
        "6. Start writing in the editor — your work auto-saves!"
      ]
    },
    { 
      id: "characters",
      title: "Character & World Building", 
      description: "Create memorable characters with depth", 
      duration: "4 min", 
      tier: "free", 
      icon: Users,
      content: [
        "1. During Story Builder, you'll be prompted to add characters",
        "2. Give each character a name, role (protagonist, antagonist, etc.)",
        "3. Add physical descriptions and personality traits",
        "4. Define relationships between characters",
        "5. For world-building, add rules and settings that make your world unique",
        "6. These details help keep your story consistent!"
      ]
    },
    { 
      id: "doctor",
      title: "Using Story Doctor", 
      description: "Find plot holes and fix pacing issues", 
      duration: "3 min", 
      tier: "free", 
      icon: Stethoscope,
      content: [
        "1. Open your book and go to the 'Story Doctor' tool",
        "2. Click 'Analyze Story' to scan your content",
        "3. Review the issues found — they're sorted by severity",
        "4. High severity = plot holes, Medium = pacing, Low = style",
        "5. Each issue includes a suggestion for how to fix it",
        "6. Make the fixes in your text, then re-analyze!"
      ]
    },
    { 
      id: "polish",
      title: "Writing Polish Tools", 
      description: "Show Don't Tell, Sensory Expansion & more", 
      duration: "5 min", 
      tier: "free", 
      icon: Wand2,
      content: [
        "1. Select text in your editor, then go to 'Writing Polish'",
        "2. Choose a tool: Show Don't Tell, Sensory, Humanizer, or Dialogue",
        "3. Show Don't Tell: Converts 'he was sad' → emotional actions",
        "4. Sensory: Adds sight, sound, smell, taste, touch details",
        "5. Humanizer: Makes prose sound more natural and less robotic",
        "6. Review the suggestion, then click 'Apply' to use it!"
      ]
    },
    { 
      id: "assisted",
      title: "Shweebum Assisted Chapters", 
      description: "Let Nova write chapters for you", 
      duration: "4 min", 
      tier: "plus", 
      icon: Sparkles,
      content: [
        "⭐ This feature requires Shweebum Plus or Premium",
        "1. Create a detailed chapter outline first",
        "2. Click 'Generate Chapter' on any outlined chapter",
        "3. Shweebum Nova writes a full chapter based on your outline",
        "4. Review and edit the generated content",
        "5. Your voice and style preferences are respected!",
        "Upgrade to Plus to unlock 100K words/month of assisted writing."
      ]
    },
    { 
      id: "fullbook",
      title: "Full Book Generation", 
      description: "One click = entire book written", 
      duration: "6 min", 
      tier: "premium", 
      icon: Zap,
      content: [
        "👑 This feature requires Shweebum Premium",
        "1. Complete your book outline with all chapters planned",
        "2. Click 'Generate Full Book' button",
        "3. Shweebum Nova writes every chapter automatically",
        "4. Perfect for rapid prototyping or overcoming writer's block",
        "5. You can edit and refine each chapter afterwards",
        "Upgrade to Premium for unlimited full book generations."
      ]
    },
  ];

  // Testimonials
  const testimonials = [
    { name: "Sarah Mitchell", location: "Columbus, Ohio", quote: "I started with just a vague idea about a romance novel. Two months later, I had a 30-chapter book. Shweebum made me believe I could actually be an author!" },
    { name: "Jake Thompson", location: "Indianapolis, Indiana", quote: "I've tried other writing tools — they felt cold and robotic. Shweebum keeps MY voice in the story. That's huge for any serious writer." },
    { name: "Emily Rodriguez", location: "Kansas City, Missouri", quote: "Free planning tools got me hooked. Once I saw how good the assisted chapters were, upgrading was a no-brainer. Worth every penny." },
    { name: "Marcus Williams", location: "Louisville, Kentucky", quote: "Six months ago, I had writer's block. Now I'm finishing my third book. My family thinks I'm a writing machine!" },
  ];

  // Writing examples
  const writingExamples = [
    {
      label: "Freebum Polish — Show Don't Tell",
      before: "John walked into the coffee shop. He was tired. He ordered coffee.",
      after: "John pushed through the door, the familiar chime announcing his arrival. The warmth of roasted beans enveloped him as exhaustion weighed on his shoulders. He raised two fingers to the barista without a word — she already knew his order.",
      isPremium: false
    },
    {
      label: "Shweebum Nova — Character Depth",
      before: "The villain laughed and said he would destroy everything.",
      after: "A cold laugh escaped Mordecai's lips, echoing through the chamber like shattering glass. 'Everything you've built,' he whispered, savoring each word as one might a fine wine, 'will crumble to ash before dawn.' His fingers traced the edge of the ancient map — the same map his father had died protecting.",
      isPremium: true
    }
  ];

  // Freebum features list
  const freebumFeatures = [
    { icon: PenTool, label: "Unlimited Writing", desc: "Write all day, every day — no word caps on YOUR writing", available: true, unlimited: true },
    { icon: Map, label: "Story Planning Studio", desc: "Chapter outlines, key moments, story structure", available: true, unlimited: true },
    { icon: Users, label: "Character & World Builder", desc: "Full character profiles, world rules, relationships", available: true, unlimited: true },
    { icon: Stethoscope, label: "Story Doctor Analysis", desc: "Find plot holes, pacing issues, consistency problems", available: true, unlimited: true },
    { icon: Eye, label: "Manuscript Analysis", desc: "Readability scores, passive voice detection", available: true, unlimited: true },
    { icon: Wand2, label: "Writing Polish Tools", desc: "Show Don't Tell, Sensory Expansion, Humanizer", available: true, unlimited: true },
    { icon: Volume2, label: "Quick Voice (Browser)", desc: "Listen to your chapters read aloud — free forever", available: true, unlimited: true },
    { icon: FileText, label: "EPUB & TXT Export", desc: "Export your book in standard formats", available: true, unlimited: true },
    { icon: Sparkles, label: "Shweebum Spark Chapters", desc: "10 assisted chapter generations", available: true, limit: `${aiGenerationsUsed}/${FREEBUM_LIMITS.aiGenerations}` },
    { icon: Download, label: "PDF Export", desc: "Download as print-ready PDF", available: true, limit: `${pdfExportsUsed}/${FREEBUM_LIMITS.pdfExports}` },
    { icon: BookOpen, label: "Book Projects", desc: "Up to 2 books, 20 chapters each", available: true, limit: `${projectCount}/${FREEBUM_LIMITS.projects}` },
  ];

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#050810]">
      {/* Ambient Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-cyan-600/5 rounded-full blur-[200px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-violet-600/5 rounded-full blur-[180px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-600/3 rounded-full blur-[250px]" />
      </div>

      {/* Header */}
      <header className="bg-[#0A0E17]/95 backdrop-blur-md border-b border-cyan-500/20 sticky top-0 z-40">
        <div className="w-full px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-3 group">
              <div className="relative">
                <div className="w-10 h-10 bg-gradient-to-br from-cyan-400/20 via-cyan-400/10 to-violet-500/20 rounded-lg flex items-center justify-center border border-cyan-500/20 shadow-lg shadow-cyan-500/20">
                  <BookOpen className="w-5 h-5 text-cyan-400" />
                  <Sparkles className="w-2.5 h-2.5 text-cyan-400 absolute -top-0.5 -right-0.5 animate-pulse" />
                </div>
              </div>
              <div className="flex flex-col">
                <span className="font-literary text-xl bg-gradient-to-r from-cyan-300 via-cyan-200 to-cyan-300 bg-clip-text text-transparent font-semibold tracking-wide">
                  Shweebum
                </span>
                <span className="text-[9px] text-slate-500 -mt-0.5 tracking-widest uppercase">Freebum Edition</span>
              </div>
            </div>

            {/* Right Side */}
            <div className="flex items-center gap-3">
              {/* Freebum Badge */}
              <div className="hidden sm:flex items-center gap-1 bg-cyan-500/15 px-3 py-1.5 rounded-full border border-cyan-500/30">
                <Feather className="w-4 h-4 text-cyan-400" />
                <span className="text-xs text-cyan-300 font-semibold">Freebum</span>
              </div>

              {/* Upgrade Button */}
              <Link to="/pricing">
                <Button size="sm" className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 shadow-lg shadow-cyan-500/25" data-testid="upgrade-btn">
                  <Zap className="w-4 h-4 mr-1" />
                  Upgrade
                </Button>
              </Link>

              {/* Settings Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white" data-testid="freebum-settings-btn">
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52 bg-slate-900 border-slate-700">
                  <DropdownMenuItem className="text-slate-300 focus:bg-slate-700">
                    <span className="text-sm">Hello, {user.name}</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-slate-700" />
                  
                  {/* Show "Full Dashboard" for paid users viewing Freebum page */}
                  {(() => {
                    const userTier = user.subscription?.toLowerCase() || 'free';
                    const paidTiers = ['storyteller', 'plus', 'pro', 'author', 'premium', 'family'];
                    const isPaidUser = paidTiers.includes(userTier);
                    if (isPaidUser) {
                      return (
                        <DropdownMenuItem 
                          className="cursor-pointer text-violet-400 focus:bg-violet-500/10"
                          onClick={() => {
                            sessionStorage.setItem('shweebum_explicit_dashboard', 'true');
                            navigate('/dashboard');
                          }}
                        >
                          <Crown className="w-4 h-4 mr-2" />
                          Full Dashboard
                          <Badge className="ml-auto text-[10px] bg-violet-500/20 text-violet-300 border-violet-500/30">PAID</Badge>
                        </DropdownMenuItem>
                      );
                    }
                    return null;
                  })()}
                  
                  <Link to="/scratchpad">
                    <DropdownMenuItem className="cursor-pointer text-emerald-400 focus:bg-emerald-500/10">
                      <PenTool className="w-4 h-4 mr-2" />
                      Free Writing Pad
                    </DropdownMenuItem>
                  </Link>
                  <Link to="/pricing">
                    <DropdownMenuItem className="cursor-pointer text-cyan-400 focus:bg-cyan-500/10">
                      <Star className="w-4 h-4 mr-2" />
                      View Plans & Pricing
                    </DropdownMenuItem>
                  </Link>
                  <DropdownMenuSeparator className="bg-slate-700" />
                  <DropdownMenuItem 
                    className="cursor-pointer text-red-400 focus:bg-red-500/10"
                    onClick={handleLogout}
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <main className="relative z-10 w-full px-4 py-6 max-w-6xl mx-auto">
        {/* Welcome Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-2 mb-4">
            <ShweebumCat size="md" />
          </div>
          <h1 className="font-literary text-3xl md:text-4xl text-white mb-2" data-testid="freebum-welcome-title">
            Welcome to <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Freebum</span>, {user.name?.split(' ')[0]}!
          </h1>
          <p className="text-slate-400 max-w-xl mx-auto">
            Your free creative writing home. Plan unlimited stories, build worlds, and bring your imagination to life.
          </p>
        </motion.div>

        {/* Share & Ambassador Section - Most Prominent */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div className={`relative overflow-hidden rounded-2xl border ${hasClaimedReward ? 'bg-gradient-to-r from-emerald-950/40 via-cyan-950/30 to-emerald-950/40 border-emerald-500/30' : 'bg-gradient-to-r from-violet-950/40 via-pink-950/30 to-cyan-950/40 border-violet-500/30'}`}>
            {/* Animated glow */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-violet-500/20 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-cyan-500/20 rounded-full blur-3xl" />
            
            <div className="relative p-6">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center shrink-0 ${hasClaimedReward ? 'bg-emerald-500/20 border border-emerald-500/40' : 'bg-violet-500/20 border border-violet-500/40'}`}>
                    {hasClaimedReward ? (
                      <Award className="w-7 h-7 text-emerald-400" />
                    ) : (
                      <Gift className="w-7 h-7 text-violet-400 animate-bounce" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h2 className="font-semibold text-white text-lg">
                        {hasClaimedReward ? "You're a Shweebum Ambassador!" : "Share Shweebum, Unlock FREE Gifts!"}
                      </h2>
                      {hasClaimedReward && (
                        <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/40">
                          Ambassador
                        </Badge>
                      )}
                    </div>
                    <p className="text-slate-400 text-sm max-w-lg">
                      {hasClaimedReward 
                        ? "Thank you for sharing! You've unlocked the Book Cover Generator, +5 extra chapters, and your Ambassador badge."
                        : "Share once, unlock forever: Book Cover Generator, +5 extra chapters, and Ambassador badge!"
                      }
                    </p>
                    {!hasClaimedReward && (
                      <div className="flex flex-wrap items-center gap-3 mt-3">
                        <span className="text-xs text-violet-400/80 flex items-center gap-1"><Palette className="w-3 h-3" /> Book Cover Generator</span>
                        <span className="text-xs text-cyan-400/80 flex items-center gap-1"><BookOpen className="w-3 h-3" /> +5 Chapters</span>
                        <span className="text-xs text-pink-400/80 flex items-center gap-1"><Award className="w-3 h-3" /> Ambassador Badge</span>
                      </div>
                    )}
                  </div>
                </div>
                <Button
                  onClick={() => setShowShareRewards(true)}
                  className={`shrink-0 ${hasClaimedReward 
                    ? 'bg-emerald-600 hover:bg-emerald-500' 
                    : 'bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 animate-pulse'
                  } text-white shadow-lg`}
                  data-testid="share-rewards-btn"
                >
                  {hasClaimedReward ? (
                    <>
                      <Share2 className="w-4 h-4 mr-2" />
                      Share More
                    </>
                  ) : (
                    <>
                      <Gift className="w-4 h-4 mr-2" />
                      Unlock FREE Gifts
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Your Freebum Features - COLLAPSIBLE DROPDOWN */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <Collapsible open={featuresOpen} onOpenChange={setFeaturesOpen}>
            <CollapsibleTrigger asChild>
              <button className="w-full flex items-center justify-between p-4 rounded-xl bg-gradient-to-r from-cyan-950/40 via-slate-900/60 to-cyan-950/40 border border-cyan-500/30 hover:border-cyan-400/50 transition-all group">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center border border-cyan-500/30">
                    <Sparkles className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div className="text-left">
                    <h2 className="font-semibold text-lg text-white">Your Freebum Features</h2>
                    <p className="text-sm text-slate-400">See what's included in your free account</p>
                  </div>
                </div>
                {featuresOpen ? (
                  <ChevronUp className="w-6 h-6 text-cyan-400" />
                ) : (
                  <ChevronDown className="w-6 h-6 text-cyan-400" />
                )}
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4 p-4 rounded-xl bg-slate-900/50 border border-cyan-500/20">
                {freebumFeatures.map((feature, idx) => (
                  <div 
                    key={idx}
                    className={`p-4 rounded-xl border transition-all ${
                      feature.available 
                        ? 'bg-slate-900/50 border-slate-700/50 hover:border-cyan-500/30' 
                        : 'bg-slate-900/30 border-slate-800/50 opacity-60'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                        feature.available ? 'bg-cyan-500/15 border border-cyan-500/30' : 'bg-slate-800 border border-slate-700'
                      }`}>
                        <feature.icon className={`w-5 h-5 ${feature.available ? 'text-cyan-400' : 'text-slate-600'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <h3 className="text-white font-medium text-sm truncate">{feature.label}</h3>
                          {feature.unlimited && (
                            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-[10px]">
                              UNLIMITED
                            </Badge>
                          )}
                        </div>
                        <p className="text-slate-400 text-xs line-clamp-2">{feature.desc}</p>
                        {feature.limit && (
                          <div className="mt-2">
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span className="text-slate-500">Used</span>
                              <span className="text-cyan-400 font-medium">{feature.limit}</span>
                            </div>
                            <Progress 
                              value={parseInt(feature.limit.split('/')[0]) / parseInt(feature.limit.split('/')[1]) * 100} 
                              className="h-1.5 bg-slate-800"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        </motion.div>

        {/* Free Write Section - No project limit - SPARKLY AND EYE-CATCHING */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.22 }}
          className="mb-8"
        >
          <Link to="/scratchpad">
            <div className="relative overflow-hidden bg-gradient-to-r from-emerald-900/60 via-teal-900/50 to-emerald-900/60 rounded-2xl border-2 border-emerald-400/50 p-6 hover:border-emerald-300 transition-all cursor-pointer group shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/40">
              {/* Sparkle decorations */}
              <div className="absolute top-2 left-4 text-yellow-400 animate-pulse">✨</div>
              <div className="absolute top-4 right-8 text-yellow-300 animate-pulse" style={{animationDelay: '0.3s'}}>✨</div>
              <div className="absolute bottom-3 left-12 text-emerald-300 animate-pulse" style={{animationDelay: '0.6s'}}>✨</div>
              <div className="absolute bottom-4 right-4 text-yellow-400 animate-pulse" style={{animationDelay: '0.9s'}}>✨</div>
              <div className="absolute top-1/2 left-2 text-teal-300 animate-pulse" style={{animationDelay: '0.5s'}}>⭐</div>
              
              <div className="flex items-center justify-between relative z-10">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-emerald-500/30 to-teal-500/30 border-2 border-emerald-400/60 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <PenTool className="w-8 h-8 text-emerald-300" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-2xl font-bold text-white">Free Writer</h3>
                      <Badge className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white border-0 text-xs px-3 py-1 animate-pulse">
                        ✨ 100% FREE ✨
                      </Badge>
                    </div>
                    <p className="text-emerald-100 text-base font-medium">
                      Click here to write for free!
                    </p>
                    <p className="text-emerald-300/80 text-sm mt-1">
                      Unlimited writing • Auto-saves • No limits • Your creative space
                    </p>
                  </div>
                </div>
                <div className="flex flex-col items-center">
                  <ChevronRight className="w-8 h-8 text-emerald-300 group-hover:translate-x-2 transition-transform" />
                  <span className="text-emerald-400 text-xs mt-1">Start Now</span>
                </div>
              </div>
            </div>
          </Link>
        </motion.div>

        {/* Start Writing CTA - PROMINENT */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="mb-8"
        >
          <div className="relative overflow-hidden bg-gradient-to-r from-cyan-950/50 via-slate-900/50 to-violet-950/50 rounded-2xl border-2 border-cyan-500/40 p-6">
            {/* Subtle glow effects */}
            <div className="absolute top-0 left-1/4 w-32 h-32 bg-cyan-500/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-1/4 w-32 h-32 bg-violet-500/10 rounded-full blur-3xl"></div>
            
            <div className="relative text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-cyan-400" />
                <h3 className="text-xl font-bold text-white">Start New Book</h3>
                <Badge className="bg-cyan-500/20 text-cyan-300 border-cyan-500/30 text-xs">
                  Shweebum Spark
                </Badge>
              </div>
              <p className="text-slate-300 text-sm mb-2">
                Experience <span className="text-cyan-400 font-medium">Shweebum Spark</span> (Intelligence) — your creative writing companion!
              </p>
              <p className="text-slate-500 text-xs mb-4">
                2 free books/month • 20 chapters each • Story Doctor • Character Builder • Writing Polish & more
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => setShowNewProject(true)}
                  className="bg-gradient-to-r from-cyan-500 to-violet-600 hover:from-cyan-400 hover:to-violet-500 shadow-xl shadow-cyan-500/30 text-white px-8 py-5 text-base font-semibold"
                  disabled={projectCount >= FREEBUM_LIMITS.projects}
                  data-testid="new-book-btn"
                >
                  <Feather className="w-5 h-5 mr-2" />
                  {projectCount >= FREEBUM_LIMITS.projects ? `Project Limit Reached (${FREEBUM_LIMITS.projects})` : 'Start New Book — FREE'}
                </Button>
                {projects.length > 0 && (
                  <div className="flex flex-col sm:flex-row gap-2">
                    {projects.map((proj) => (
                      <Button
                        key={proj.id}
                        variant="outline"
                        className="border-slate-600 text-slate-300 hover:bg-slate-800"
                        onClick={() => navigate(`/freebum/write/${proj.id}`)}
                      >
                        <BookOpen className="w-4 h-4 mr-2" />
                        Continue "{proj?.title?.slice(0, 20)}{proj?.title?.length > 20 ? '...' : ''}"
                      </Button>
                    ))}
                  </div>
                )}
              </div>
              {projectCount >= FREEBUM_LIMITS.projects && (
                <p className="text-xs text-amber-400 mt-3">
                  <Link to="/pricing" className="underline hover:text-amber-300">Upgrade to Plus</Link> for up to 10 projects!
                </p>
              )}
            </div>
          </div>
        </motion.div>

        {/* SHARE & UNLOCK FEATURES - Prominent Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.27 }}
          className="mb-8"
        >
          <div className="relative overflow-hidden rounded-2xl border-2 border-pink-500/40 bg-gradient-to-r from-pink-950/40 via-slate-900/60 to-orange-950/40">
            {/* Sparkle effects */}
            <div className="absolute top-2 left-6 text-yellow-400 animate-pulse">✨</div>
            <div className="absolute top-4 right-10 text-pink-300 animate-pulse" style={{animationDelay: '0.5s'}}>✨</div>
            <div className="absolute bottom-4 left-1/3 text-orange-300 animate-pulse" style={{animationDelay: '0.8s'}}>✨</div>
            
            <div className="p-6 relative">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-pink-500/30 to-orange-500/30 border-2 border-pink-400/50 flex items-center justify-center shadow-lg shadow-pink-500/20">
                    <Share2 className="w-7 h-7 text-pink-300" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                      Share & Unlock FREE Features!
                      {hasClaimedReward && (
                        <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                          <Check className="w-3 h-3 mr-1" /> Unlocked!
                        </Badge>
                      )}
                    </h3>
                    <p className="text-pink-200/80 text-sm">
                      {hasClaimedReward 
                        ? "You've unlocked: Book Cover Creator, +5 Chapters, Ambassador Badge!" 
                        : "Share Shweebum with friends and unlock the Book Cover Creator, +5 bonus chapters & more!"}
                    </p>
                  </div>
                </div>
                
                <Button
                  onClick={() => setShowShareRewards(true)}
                  className={`px-6 py-5 text-base font-semibold shadow-xl ${
                    hasClaimedReward 
                      ? 'bg-emerald-600 hover:bg-emerald-500 shadow-emerald-500/30' 
                      : 'bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-400 hover:to-orange-400 shadow-pink-500/30'
                  }`}
                >
                  {hasClaimedReward ? (
                    <>
                      <Gift className="w-5 h-5 mr-2" />
                      Use Your Free Features
                    </>
                  ) : (
                    <>
                      <Share2 className="w-5 h-5 mr-2" />
                      Share & Unlock Now
                    </>
                  )}
                </Button>
              </div>
              
              {/* Unlocked Features Preview (if claimed) */}
              {hasClaimedReward && (
                <div className="mt-4 pt-4 border-t border-pink-500/20">
                  <div className="flex flex-wrap gap-3">
                    <div 
                      onClick={() => setShowShareRewards(true)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 cursor-pointer hover:bg-emerald-500/20 transition-all"
                    >
                      <Palette className="w-4 h-4 text-emerald-400" />
                      <span className="text-emerald-300 text-sm font-medium">Book Cover Creator</span>
                      <Badge className="bg-emerald-500 text-white text-[10px]">FREE</Badge>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                      <BookOpen className="w-4 h-4 text-emerald-400" />
                      <span className="text-emerald-300 text-sm font-medium">+5 Bonus Chapters</span>
                      <Badge className="bg-emerald-500 text-white text-[10px]">FREE</Badge>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                      <Award className="w-4 h-4 text-emerald-400" />
                      <span className="text-emerald-300 text-sm font-medium">Ambassador Badge</span>
                      <Badge className="bg-emerald-500 text-white text-[10px]">FREE</Badge>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Scrolling Feature Banners - Collapsible */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-8"
        >
          <Collapsible open={plansOpen} onOpenChange={setPlansOpen}>
            <CollapsibleTrigger asChild>
              <button className="w-full flex items-center justify-between p-4 rounded-xl bg-gradient-to-r from-violet-950/40 via-slate-900/60 to-cyan-950/40 border border-violet-500/30 hover:border-violet-400/50 transition-all group">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500/20 to-violet-500/20 flex items-center justify-center border border-violet-500/30">
                    <Crown className="w-5 h-5 text-violet-400" />
                  </div>
                  <div className="text-left">
                    <h2 className="font-semibold text-lg text-white">Shweebum Nova <span className="text-violet-400 text-sm">(Ultra Intelligence)</span></h2>
                    <p className="text-sm text-slate-400">Unlock unlimited power with Plus & Premium</p>
                  </div>
                </div>
                {plansOpen ? (
                  <ChevronUp className="w-6 h-6 text-violet-400" />
                ) : (
                  <ChevronDown className="w-6 h-6 text-violet-400" />
                )}
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="space-y-3 mt-4">
                {/* Plus Banner */}
                <div className="rounded-xl overflow-hidden border border-cyan-500/30">
                  <div className="bg-gradient-to-r from-cyan-600/20 via-blue-600/20 to-cyan-600/20 px-4 py-2 border-b border-cyan-500/20">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Star className="w-4 h-4 text-cyan-400" />
                        <span className="text-cyan-300 font-semibold text-sm">Shweebum Plus</span>
                        <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30 text-[10px]">Nova</Badge>
                        <span className="text-slate-400 text-xs">$10/month</span>
                      </div>
                      <Link to="/pricing">
                        <Button size="sm" variant="ghost" className="text-cyan-400 hover:text-cyan-300 text-xs h-7">
                          Learn More <ChevronRight className="w-3 h-3 ml-1" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                  <ScrollingBanner 
                    features={plusFeatures}
                    tier="Plus"
                    gradient="bg-gradient-to-r from-slate-900 via-cyan-950/30 to-slate-900"
                    textColor="text-cyan-300"
                    speed={40}
                  />
                </div>

                {/* Premium Banner */}
                <div className="rounded-xl overflow-hidden border border-violet-500/30">
                  <div className="bg-gradient-to-r from-violet-600/20 via-purple-600/20 to-violet-600/20 px-4 py-2 border-b border-violet-500/20">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Crown className="w-4 h-4 text-violet-400" />
                        <span className="text-violet-300 font-semibold text-sm">Shweebum Premium</span>
                        <Badge className="bg-violet-500/20 text-violet-400 border-violet-500/30 text-[10px]">Nova Ultra</Badge>
                        <span className="text-slate-400 text-xs">$20/month</span>
                      </div>
                      <Link to="/pricing">
                        <Button size="sm" variant="ghost" className="text-violet-400 hover:text-violet-300 text-xs h-7">
                          Learn More <ChevronRight className="w-3 h-3 ml-1" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                  <ScrollingBanner 
                    features={premiumFeatures}
                    tier="Premium"
                    gradient="bg-gradient-to-r from-slate-900 via-violet-950/30 to-slate-900"
                    textColor="text-violet-300"
                    speed={35}
                  />
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </motion.div>

        {/* Tutorials Section - Collapsible Dropdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="mb-8"
        >
          <Collapsible open={tutorialsOpen} onOpenChange={setTutorialsOpen}>
            <CollapsibleTrigger asChild>
              <button className="w-full flex items-center justify-between p-4 rounded-xl bg-gradient-to-r from-violet-950/40 via-slate-900/60 to-violet-950/40 border border-violet-500/30 hover:border-violet-400/50 transition-all group">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center border border-violet-500/30">
                    <HelpCircle className="w-5 h-5 text-violet-400" />
                  </div>
                  <div className="text-left">
                    <h2 className="font-semibold text-lg text-white">Learn Shweebum</h2>
                    <p className="text-sm text-slate-400">Tutorials to help you get started</p>
                  </div>
                </div>
                {tutorialsOpen ? (
                  <ChevronUp className="w-6 h-6 text-violet-400" />
                ) : (
                  <ChevronDown className="w-6 h-6 text-violet-400" />
                )}
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4 p-4 rounded-xl bg-slate-900/50 border border-violet-500/20">
                {tutorials.map((tutorial, idx) => (
                  <TutorialCard 
                    key={idx} 
                    {...tutorial} 
                    onClick={() => setShowTutorial(tutorial)}
                  />
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        </motion.div>

        {/* See the Magic - NEON GLOW EFFECT */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-8"
        >
          <div className="relative">
            {/* Neon glow background */}
            <div className="absolute inset-0 bg-gradient-to-r from-pink-500/20 via-yellow-500/20 to-pink-500/20 blur-xl rounded-3xl"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-emerald-500/10 to-cyan-500/10 blur-2xl rounded-3xl animate-pulse"></div>
            
            <div className="relative p-6 rounded-2xl bg-gradient-to-r from-slate-900/90 via-slate-800/90 to-slate-900/90 border-2 border-yellow-400/50 shadow-[0_0_30px_rgba(250,204,21,0.3),0_0_60px_rgba(236,72,153,0.2)]">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-500/30 to-pink-500/30 flex items-center justify-center border-2 border-yellow-400/60 shadow-[0_0_15px_rgba(250,204,21,0.5)]">
                  <Lightbulb className="w-5 h-5 text-yellow-300" />
                </div>
                <div>
                  <h2 className="font-bold text-xl text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-pink-300 to-yellow-300 animate-pulse">
                    ✨ See the Magic in Action ✨
                  </h2>
                  <p className="text-sm text-yellow-200/70">Watch Shweebum transform your ideas</p>
                </div>
              </div>

              <div className="space-y-4">
                {writingExamples.map((example, idx) => (
                  <WritingExample key={idx} {...example} />
                ))}
              </div>
              
              <div className="mt-6 text-center">
                <p className="text-yellow-200/80 text-sm mb-3">Ready to experience this magic yourself?</p>
                <Link to="/pricing">
                  <Button className="bg-gradient-to-r from-yellow-500 via-pink-500 to-yellow-500 hover:from-yellow-400 hover:to-pink-400 text-black font-bold px-8 shadow-[0_0_20px_rgba(250,204,21,0.5)]">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Unlock the Magic — Upgrade Now
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Reviews Section - Collapsible Dropdown with Submit Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="mb-8"
        >
          <Collapsible open={reviewsOpen} onOpenChange={setReviewsOpen}>
            <CollapsibleTrigger asChild>
              <button className="w-full flex items-center justify-between p-4 rounded-xl bg-gradient-to-r from-amber-950/40 via-slate-900/60 to-amber-950/40 border border-amber-500/30 hover:border-amber-400/50 transition-all group">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center border border-amber-500/30">
                    <Star className="w-5 h-5 text-amber-400" />
                  </div>
                  <div className="text-left">
                    <h2 className="font-semibold text-lg text-white">Reviews & Testimonials</h2>
                    <p className="text-sm text-slate-400">See what writers are saying • Submit your review</p>
                  </div>
                </div>
                {reviewsOpen ? (
                  <ChevronUp className="w-6 h-6 text-amber-400" />
                ) : (
                  <ChevronDown className="w-6 h-6 text-amber-400" />
                )}
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-4 p-4 rounded-xl bg-slate-900/50 border border-amber-500/20">
                {/* Existing Testimonials */}
                <div className="grid sm:grid-cols-2 gap-4 mb-6">
                  {testimonials.map((testimonial, idx) => (
                    <TestimonialCard key={idx} {...testimonial} />
                  ))}
                </div>
                
                {/* Submit Review Section */}
                <div className="border-t border-amber-500/20 pt-6">
                  <div className="flex items-center gap-2 mb-4">
                    <MessageSquare className="w-5 h-5 text-amber-400" />
                    <h3 className="font-semibold text-white">Submit Your Review</h3>
                  </div>
                  <p className="text-sm text-slate-400 mb-4">
                    Share your experience with Shweebum! Reviews will be displayed after being reviewed by our team.
                  </p>
                  
                  <div className="space-y-4 max-w-lg">
                    <div>
                      <Label className="text-slate-300">Your Email</Label>
                      <Input
                        type="email"
                        placeholder="your@email.com"
                        value={reviewForm.email}
                        onChange={(e) => setReviewForm({...reviewForm, email: e.target.value})}
                        className="bg-slate-800 border-slate-600 text-white mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-slate-300">Rating</Label>
                      <div className="flex gap-2 mt-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            onClick={() => setReviewForm({...reviewForm, rating: star})}
                            className="transition-transform hover:scale-110"
                          >
                            <Star 
                              className={`w-8 h-8 ${star <= reviewForm.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-600'}`} 
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-slate-300">Would you recommend Shweebum?</Label>
                      <div className="flex gap-4 mt-2">
                        <Button
                          type="button"
                          variant={reviewForm.recommend ? "default" : "outline"}
                          className={reviewForm.recommend ? "bg-emerald-600 hover:bg-emerald-500" : "border-slate-600"}
                          onClick={() => setReviewForm({...reviewForm, recommend: true})}
                        >
                          <ThumbsUp className="w-4 h-4 mr-2" /> Yes!
                        </Button>
                        <Button
                          type="button"
                          variant={!reviewForm.recommend ? "default" : "outline"}
                          className={!reviewForm.recommend ? "bg-red-600 hover:bg-red-500" : "border-slate-600"}
                          onClick={() => setReviewForm({...reviewForm, recommend: false})}
                        >
                          Not Yet
                        </Button>
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-slate-300">Your Review</Label>
                      <Textarea
                        placeholder="Tell us about your experience with Shweebum..."
                        value={reviewForm.reviewText}
                        onChange={(e) => setReviewForm({...reviewForm, reviewText: e.target.value})}
                        className="bg-slate-800 border-slate-600 text-white mt-1 min-h-[100px]"
                      />
                    </div>
                    
                    <Button
                      onClick={handleSubmitReview}
                      disabled={submittingReview}
                      className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black font-semibold"
                    >
                      {submittingReview ? (
                        <>Submitting...</>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Submit Review
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="text-center pb-8"
        >
          <div className="bg-gradient-to-r from-violet-950/50 via-purple-950/30 to-cyan-950/50 rounded-2xl border border-violet-500/20 p-8">
            <Crown className="w-12 h-12 text-violet-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-2">Ready to Unlock Your Full Potential?</h3>
            <p className="text-slate-400 mb-6 max-w-lg mx-auto">
              Join thousands of writers who've upgraded to Plus or Premium. 
              Write entire books, create stunning covers, and let Shweebum Nova bring your stories to life.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/pricing">
                <Button className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white px-8 py-6 text-lg shadow-xl shadow-cyan-500/30">
                  <Star className="w-5 h-5 mr-2" />
                  Get Shweebum Plus — $10/mo
                </Button>
              </Link>
              <Link to="/pricing">
                <Button variant="outline" className="border-violet-500/40 text-violet-300 hover:bg-violet-500/10 px-8 py-6 text-lg">
                  <Crown className="w-5 h-5 mr-2" />
                  Go Premium — $20/mo
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="border-t border-cyan-500/20 py-6 bg-[#0A0E17]">
        <div className="container mx-auto px-4 text-center text-slate-500 text-sm">
          <p>&copy; {new Date().getFullYear()} Shweebum — You Inspire Designs LLC. All rights reserved.</p>
        </div>
      </footer>

      {/* Modals */}
      <ShareRewardsModal
        open={showShareRewards}
        onClose={() => setShowShareRewards(false)}
        user={user}
        projects={projects}
        onRewardsClaimed={() => {
          setHasClaimedReward(true);
          checkRewardStatus();
        }}
      />

      <NewProjectDialog
        open={showNewProject}
        onOpenChange={setShowNewProject}
        genres={genres}
        userId={user?.id}
        freebumMode={true}
        onCreated={(project) => {
          setProjects([project, ...projects]);
          setShowNewProject(false);
          navigate(`/freebum/write/${project.id}`);
        }}
      />

      {/* Tutorial Modal */}
      <Dialog open={!!showTutorial} onOpenChange={(open) => !open && setShowTutorial(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-lg">
          <DialogHeader>
            <div className="flex items-center gap-3">
              {showTutorial && (
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  showTutorial.tier === 'free' ? 'bg-emerald-500/20 border border-emerald-500/30' :
                  showTutorial.tier === 'plus' ? 'bg-cyan-500/20 border border-cyan-500/30' :
                  'bg-violet-500/20 border border-violet-500/30'
                }`}>
                  {showTutorial && <showTutorial.icon className={`w-5 h-5 ${
                    showTutorial.tier === 'free' ? 'text-emerald-400' :
                    showTutorial.tier === 'plus' ? 'text-cyan-400' :
                    'text-violet-400'
                  }`} />}
                </div>
              )}
              <div>
                <DialogTitle className="text-white">{showTutorial?.title}</DialogTitle>
                <DialogDescription className="text-slate-400 text-sm">
                  {showTutorial?.description}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          
          {showTutorial && (
            <div className="space-y-3 mt-4">
              {/* Tier badge */}
              <div className="flex items-center gap-2">
                {showTutorial.tier === 'free' && (
                  <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                    <Check className="w-3 h-3 mr-1" /> Available Now
                  </Badge>
                )}
                {showTutorial.tier === 'plus' && (
                  <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30">
                    <Star className="w-3 h-3 mr-1" /> Requires Plus
                  </Badge>
                )}
                {showTutorial.tier === 'premium' && (
                  <Badge className="bg-violet-500/20 text-violet-400 border-violet-500/30">
                    <Crown className="w-3 h-3 mr-1" /> Requires Premium
                  </Badge>
                )}
                <span className="text-xs text-slate-500 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {showTutorial.duration}
                </span>
              </div>

              {/* Tutorial content */}
              <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                <ul className="space-y-2.5">
                  {showTutorial.content?.map((step, idx) => (
                    <li key={idx} className={`text-sm ${
                      step.startsWith('⭐') || step.startsWith('👑') 
                        ? 'text-amber-400 font-medium' 
                        : 'text-slate-300'
                    }`}>
                      {step}
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA for premium tutorials */}
              {showTutorial.tier !== 'free' && (
                <div className={`p-3 rounded-lg border ${
                  showTutorial.tier === 'plus' 
                    ? 'bg-cyan-950/30 border-cyan-500/30' 
                    : 'bg-violet-950/30 border-violet-500/30'
                }`}>
                  <p className={`text-sm mb-2 ${
                    showTutorial.tier === 'plus' ? 'text-cyan-300' : 'text-violet-300'
                  }`}>
                    {showTutorial.tier === 'plus' 
                      ? '✨ Upgrade to Shweebum Plus to unlock this feature!'
                      : '👑 Upgrade to Shweebum Premium for the ultimate writing experience!'
                    }
                  </p>
                  <Link to="/pricing">
                    <Button 
                      size="sm" 
                      className={showTutorial.tier === 'plus' 
                        ? 'bg-cyan-600 hover:bg-cyan-500' 
                        : 'bg-violet-600 hover:bg-violet-500'
                      }
                    >
                      View Plans
                    </Button>
                  </Link>
                </div>
              )}

              {/* Try it now for free tutorials */}
              {showTutorial.tier === 'free' && (
                <Button
                  onClick={() => {
                    setShowTutorial(null);
                    if (projects.length > 0) {
                      navigate(`/freebum/write/${projects[0].id}`);
                    } else {
                      setShowNewProject(true);
                    }
                  }}
                  className="w-full bg-emerald-600 hover:bg-emerald-500"
                >
                  <Play className="w-4 h-4 mr-2" />
                  {projects.length > 0 ? 'Try It Now' : 'Start a Project'}
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FreebumPage;
